import { useProjectId } from '@consigli/hooks';
import { Sparkle } from 'lucide-react';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '@/components/empty-state';
import { useProjectServices } from '@/features/project-services/use-project-services';

import { AgentCardGrid } from './agent-card-grid';

/**
 * Section label: the frame's `paragraph small/medium`, 14/20 at weight 500.
 *
 * `raw-stone-600` is the frame's text-secondary. The exported
 * --ds-color-text-secondary is #78716C where the frame draws #57534E, so the
 * raw step is what matches — the same call agent-card.tsx and
 * agents-directory-card.tsx make, and the same drift they are waiting on.
 * Using the named token here would leave this label a different grey from the
 * card descriptions directly beneath it.
 *
 * pb-3 is the frame's 12: the heading row ends at y=20 and the grid starts at
 * y=32. An earlier frame drew 36 above one grid and 32 above the other, which
 * was the inconsistency this replaces rather than a distinction to keep.
 */
const SECTION_HEADING =
  'flex items-center gap-2 pb-3 text-sm leading-5 font-medium text-[var(--ds-color-raw-stone-600)]';

/**
 * The frame's `Icon / sparkle`, flattened to a fill there. Lucide's `Sparkle`
 * is the single four-pointed star it draws — the exported SVG has one path, and
 * `Sparkles`, which the side nav uses for its Agents row, is three stars and a
 * different mark. 1.5 is the stroke the other Lucide marks in the redesign are
 * drawn at.
 *
 * Decorative: the heading beside it already says what the section is. The frame
 * sets it at x=0 against text at x=24, which is the gap-2 above, and centres it
 * in the 20 line box.
 */
const SectionMark: FC = () => (
  <Sparkle aria-hidden="true" className="size-4 shrink-0" strokeWidth={1.5} />
);

/**
 * The Agents tab body: the project's agents, and what else it could have.
 *
 * No loading state of its own — `useProjectServices` reads the project query the
 * route already waited on, so it is in cache by the time this renders.
 */
export const AgentsTab: FC = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const { services } = useProjectServices();

  const applied = services.filter((service) => service.active);
  const available = services.filter((service) => !service.active);

  return (
    <div className="flex flex-col gap-10 py-8">
      {applied.length > 0 && (
        <section>
          <h2 className={SECTION_HEADING}>
            <SectionMark />
            <span>{t('project-overview.agents-applied', { count: applied.length })}</span>
          </h2>
          <AgentCardGrid projectId={projectId} services={applied} />
        </section>
      )}

      {available.length > 0 && (
        <section>
          <h2 className={SECTION_HEADING}>
            <SectionMark />
            <span>{t('project-overview.agents-available', { count: available.length })}</span>
          </h2>
          <AgentCardGrid projectId={projectId} services={available} />
        </section>
      )}

      {/*
       * Only when both lists are empty — a new project shows the available
       * section alone, which is the common case, not an empty tab.
       *
       * Reached three ways: no project access, a role that is neither ADMIN nor
       * USER, or an org with no available services. The copy names none of them,
       * since only the first is "you lack access".
       *
       * EmptyState and not a warning Callout, which project-overview-error.tsx
       * already uses for "no access to this project".
       */}
      {services.length === 0 && <EmptyState description={t('project-overview.no-agents')} />}
    </div>
  );
};
